import React, { useRef, useEffect, useState, useMemo } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#ffffff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  title: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#111827',
    margin: 0,
  },
  subtitle: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '4px 0 16px 0',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
  },
  label: {
    fontSize: '13px',
    fontWeight: 500,
    color: '#374151',
  },
  select: {
    padding: '6px 10px',
    fontSize: '13px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    background: '#ffffff',
    color: '#111827',
    cursor: 'pointer',
    outline: 'none',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '5px 12px',
    borderRadius: '16px',
    fontSize: '12px',
    fontWeight: 600,
    marginLeft: 'auto',
  },
  badgeConforms: {
    background: '#dcfce7',
    color: '#166534',
    border: '1px solid #bbf7d0',
  },
  badgeDeviates: {
    background: '#fee2e2',
    color: '#991b1b',
    border: '1px solid #fecaca',
  },
  svgContainer: {
    width: '100%',
    maxWidth: '700px',
    margin: '0 auto',
    position: 'relative',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#1f2937',
    color: '#f9fafb',
    padding: '10px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    lineHeight: '1.5',
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    zIndex: 9999,
    whiteSpace: 'nowrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '14px',
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    justifyContent: 'center',
    marginTop: '8px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
  },
  legendSwatch: {
    width: '12px',
    height: '12px',
    borderRadius: '2px',
  },
};

const FIELD_LABELS = {
  pest365: 'Pest 365',
  pest365Plus: 'Pest 365 Plus',
  oneTime: 'One-Time',
  initialServiceVisit: 'Initial Service Visit',
  mosquitoTickControl: 'Mosquito & Tick Control',
};

export default function BenfordAnalysis({ data }) {
  const svgRef = useRef(null);
  const [selectedField, setSelectedField] = useState('');
  const [tooltip, setTooltip] = useState(null);

  const fieldKeys = useMemo(() => {
    if (!data || !data.fields) return [];
    return Object.keys(data.fields);
  }, [data]);

  useEffect(() => {
    if (fieldKeys.length > 0 && !selectedField) {
      setSelectedField(fieldKeys[0]);
    }
  }, [fieldKeys, selectedField]);

  const fieldData = useMemo(() => {
    if (!data || !data.fields || !selectedField) return null;
    return data.fields[selectedField];
  }, [data, selectedField]);

  useEffect(() => {
    if (!svgRef.current || !fieldData) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = 700;
    const height = 350;
    const margin = { top: 30, right: 30, bottom: 45, left: 50 };
    const innerWidth = width - margin.left - margin.right;
    const innerHeight = height - margin.top - margin.bottom;

    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const digits = fieldData.digits;
    const observed = fieldData.observed;
    const expected = fieldData.expected;

    // Scales
    const xScale = d3.scaleBand()
      .domain(digits.map(String))
      .range([0, innerWidth])
      .paddingOuter(0.15)
      .paddingInner(0.3);

    const maxY = Math.max(
      d3.max(observed),
      d3.max(expected),
      0.35
    );

    const yScale = d3.scaleLinear()
      .domain([0, maxY])
      .range([innerHeight, 0])
      .nice();

    // Grid lines
    const yTicks = yScale.ticks(7);
    g.selectAll('.grid-line')
      .data(yTicks)
      .enter()
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('y1', d => yScale(d))
      .attr('y2', d => yScale(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Bar width for grouped bars
    const bandwidth = xScale.bandwidth();
    const barWidth = bandwidth * 0.42;
    const barGap = bandwidth * 0.04;

    // Expected bars (gray dashed outline)
    digits.forEach((digit, i) => {
      const x = xScale(String(digit));
      const barX = x + bandwidth / 2 + barGap / 2;
      const barY = yScale(expected[i]);
      const barH = innerHeight - barY;

      g.append('rect')
        .attr('x', barX)
        .attr('y', barY)
        .attr('width', barWidth)
        .attr('height', barH)
        .attr('fill', 'none')
        .attr('stroke', '#9ca3af')
        .attr('stroke-width', 1.5)
        .attr('stroke-dasharray', '4,3')
        .attr('rx', 2);
    });

    // Observed bars (blue)
    digits.forEach((digit, i) => {
      const x = xScale(String(digit));
      const barX = x;
      const barY = yScale(observed[i]);
      const barH = innerHeight - barY;

      g.append('rect')
        .attr('x', barX)
        .attr('y', barY)
        .attr('width', barWidth)
        .attr('height', barH)
        .attr('fill', '#3b82f6')
        .attr('rx', 2)
        .attr('opacity', 0.85);
    });

    // Benford's expected line (connected dots)
    const lineGenerator = d3.line()
      .x((d, i) => xScale(String(digits[i])) + bandwidth / 2)
      .y(d => yScale(d))
      .curve(d3.curveMonotoneX);

    g.append('path')
      .datum(expected)
      .attr('d', lineGenerator)
      .attr('fill', 'none')
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 2)
      .attr('stroke-dasharray', '6,3');

    g.selectAll('.expected-dot')
      .data(expected)
      .enter()
      .append('circle')
      .attr('cx', (d, i) => xScale(String(digits[i])) + bandwidth / 2)
      .attr('cy', d => yScale(d))
      .attr('r', 4)
      .attr('fill', '#ffffff')
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 2);

    // Invisible hover rects for tooltip
    digits.forEach((digit, i) => {
      const x = xScale(String(digit));

      g.append('rect')
        .attr('x', x - bandwidth * 0.1)
        .attr('y', 0)
        .attr('width', bandwidth * 1.2)
        .attr('height', innerHeight)
        .attr('fill', 'transparent')
        .attr('cursor', 'pointer')
        .on('mousemove', (event) => {
          const deviation = ((observed[i] - expected[i]) / expected[i]) * 100;
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            digit,
            observed: observed[i],
            expected: expected[i],
            deviation,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    // X axis
    const xAxis = d3.axisBottom(xScale).tickSize(0).tickPadding(10);
    g.append('g')
      .attr('transform', `translate(0,${innerHeight})`)
      .call(xAxis)
      .call(g => g.select('.domain').attr('stroke', '#d1d5db'))
      .selectAll('text')
      .attr('fill', '#6b7280')
      .attr('font-size', '12px');

    // X axis label
    g.append('text')
      .attr('x', innerWidth / 2)
      .attr('y', innerHeight + 38)
      .attr('text-anchor', 'middle')
      .attr('fill', '#9ca3af')
      .attr('font-size', '12px')
      .text('Leading Digit');

    // Y axis
    const yAxis = d3.axisLeft(yScale)
      .ticks(7)
      .tickFormat(d => `${(d * 100).toFixed(0)}%`)
      .tickSize(0)
      .tickPadding(8);

    g.append('g')
      .call(yAxis)
      .call(g => g.select('.domain').attr('stroke', '#d1d5db'))
      .selectAll('text')
      .attr('fill', '#6b7280')
      .attr('font-size', '11px');

  }, [fieldData]);

  if (!data || !data.fields || Object.keys(data.fields).length === 0) {
    return (
      <div style={styles.card}>
        <h3 style={styles.title}>Benford's Law Analysis</h3>
        <p style={styles.subtitle}>Leading digit distribution vs theoretical expectation</p>
        <div style={styles.emptyState}>No data available for Benford analysis.</div>
      </div>
    );
  }

  const currentField = fieldData;
  const badgeStyle = currentField && currentField.conforms
    ? { ...styles.badge, ...styles.badgeConforms }
    : { ...styles.badge, ...styles.badgeDeviates };

  return (
    <div style={styles.card}>
      <h3 style={styles.title}>Benford's Law Analysis</h3>
      <p style={styles.subtitle}>Leading digit distribution vs theoretical expectation</p>

      <div style={styles.controls}>
        <span style={styles.label}>Price Field:</span>
        <select
          style={styles.select}
          value={selectedField}
          onChange={e => setSelectedField(e.target.value)}
        >
          {fieldKeys.map(key => (
            <option key={key} value={key}>
              {FIELD_LABELS[key] || key}
            </option>
          ))}
        </select>

        {currentField && (
          <span style={badgeStyle}>
            {currentField.conforms ? '\u2713' : '\u2717'}
            {currentField.conforms ? 'Conforms to Benford' : 'Deviates from Benford'}
            <span style={{ fontWeight: 400, opacity: 0.8, marginLeft: '4px' }}>
              (p = {currentField.pValue.toFixed(2)}, \u03C7\u00B2 = {currentField.chiSquare.toFixed(2)})
            </span>
          </span>
        )}
      </div>

      <div style={styles.svgContainer}>
        <svg
          ref={svgRef}
          viewBox="0 0 700 350"
          preserveAspectRatio="xMidYMid meet"
          style={{ width: '100%', height: 'auto', display: 'block' }}
        />
      </div>

      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <div style={{ ...styles.legendSwatch, background: '#3b82f6', opacity: 0.85 }} />
          <span>Observed</span>
        </div>
        <div style={styles.legendItem}>
          <div style={{
            ...styles.legendSwatch,
            background: 'none',
            border: '1.5px dashed #9ca3af',
          }} />
          <span>Expected (Benford)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="12" style={{ display: 'block' }}>
            <line x1="0" y1="6" x2="14" y2="6" stroke="#6b7280" strokeWidth="2" strokeDasharray="4,2" />
            <circle cx="17" cy="6" r="3" fill="#fff" stroke="#6b7280" strokeWidth="1.5" />
          </svg>
          <span>Benford's Line</span>
        </div>
      </div>

      {tooltip && (
        <div style={{ ...styles.tooltip, left: tooltip.x, top: tooltip.y }}>
          <div style={{ fontWeight: 600, marginBottom: '4px' }}>
            Digit {tooltip.digit}
          </div>
          <div>Observed: {(tooltip.observed * 100).toFixed(1)}%</div>
          <div>Expected: {(tooltip.expected * 100).toFixed(1)}%</div>
          <div style={{
            color: Math.abs(tooltip.deviation) > 10 ? '#fca5a5' : '#86efac',
            marginTop: '2px',
          }}>
            Deviation: {tooltip.deviation > 0 ? '+' : ''}{tooltip.deviation.toFixed(1)}%
          </div>
        </div>
      )}
    </div>
  );
}
